import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'

interface FormResetButtonProps {
  /** Overrides the default wording where a form needs something more specific. */
  label?: string
  maxWidth?: boolean
}

/**
 * Puts a form back to the values it was loaded with.
 *
 * Pass it through `DynamicForm`'s `FormActions` slot rather than rendering it
 * beside the form: it reaches the form through `useFormContext`, and outside the
 * provider there is nothing for it to reset.
 *
 * `reset()` takes no argument on purpose. `DynamicForm` re-runs `reset(values)`
 * whenever its `defaultValues` change, which makes the last loaded server state
 * the form's defaults, so this always returns to what is actually saved rather
 * than to whatever was there when the page first mounted.
 */
function FormResetButton(props: FormResetButtonProps) {
  const { label, maxWidth } = props
  const { t } = useTranslation()
  const { reset, formState } = useFormContext()

  return (
    <Button
      label={label ?? t('COMMON.REVERT')}
      variant="outlined"
      color="primary"
      maxWidth={maxWidth}
      // Nothing to undo on an untouched form, and an enabled button there reads
      // as if it would discard what is saved.
      disabled={!formState.isDirty}
      onClick={() => reset()}
    />
  )
}

export default FormResetButton
